import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEnvelope, FaGraduationCap, FaChalkboardTeacher } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://learnify-58mi.onrender.com/api'
  : 'http://localhost:5000/api';

const Auth = () => {
    const navigate = useNavigate();
    const { login, user } = useAuth();
    const [isLogin, setIsLogin] = useState(true);
    const [role, setRole] = useState('student');
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        confirmPassword: '',
        expertise: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!isLogin && formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (isLogin) {
            try {
                const response = await fetch(`${API_BASE_URL}/auth/login`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        email: formData.email,
                        password: formData.password
                    })
                });
                const responseText = await response.text();
                let data;
                try {
                    data = JSON.parse(responseText);
                } catch (e) {
                    throw new Error('Invalid response from server');
                }
                if (!response.ok) {
                    throw new Error(data.message || 'Login failed');
                }
                localStorage.setItem('token', data.token);
                localStorage.setItem('role', data.user?.role || 'student');
                if (data.user) {
                    localStorage.setItem('user', JSON.stringify(data.user));
                }
                await login(formData.email, formData.password);
                navigate(data.user?.role === 'instructor' ? '/instructorDashboard' : '/dashboard');
            } catch (err) {
                setError(err.message || 'Login failed. Please try again.');
            } finally {
                setLoading(false);
            }
        } else {
            try {
                const res = await fetch(`${API_BASE_URL}/auth/register`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email,
                        password: formData.password,
                        role: role,
                        ...(role === 'instructor' && { expertise: formData.expertise })
                    }),
                });
                const data = await res.json();
                if (!res.ok) {
                    setError(data.message || 'Registration failed.');
                    return;
                }
                setIsLogin(true);
                setError('Registration successful! Please log in.');
            } catch (err) {
                setError('Registration failed. Please try again.');
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        if (user) {
            navigate(user.role === 'instructor' ? '/instructorDashboard' : '/dashboard');
        }
    }, [user, navigate]);

    return (
        <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center bg-light px-0">
            <div className="row w-100 justify-content-center mx-0">
                <div className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4 px-2 px-sm-0">
                    <div className="card shadow border-0 rounded-4 overflow-hidden">
                        <div className="card-header bg-primary text-white text-center py-4">
                            <h2 className="mb-1 fw-bold" style={{ fontSize: '2rem' }}>
                                {isLogin ? 'Sign In' : 'Create Account'}
                            </h2>
                            <p className="mb-0 small">
                                {isLogin 
                                    ? 'Welcome back! Please sign in to continue.' 
                                    : 'Please fill in your details to register.'}
                            </p>
                        </div>
                        <div className="card-body p-4 p-md-5">
                            {/* Role Selection */}
                            <div className="role-selector mb-4 d-flex justify-content-center">
                                <div className="btn-group w-100" role="group">
                                    <button
                                        type="button"
                                        className={`btn ${role === 'student' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setRole('student')}
                                        style={{ minWidth: 120 }}
                                    >
                                        <FaGraduationCap className="me-2" />
                                        Student
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${role === 'instructor' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setRole('instructor')}
                                        style={{ minWidth: 120 }}
                                    >
                                        <FaChalkboardTeacher className="me-2" />
                                        Instructor
                                    </button>
                                </div>
                            </div>
                            <form onSubmit={handleSubmit} autoComplete="off">
                                {!isLogin && (
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="name"
                                            name="name"
                                            placeholder="Full Name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            autoComplete="off"
                                        />
                                        <label htmlFor="name">
                                            <FaUser className="me-2" />
                                            Full Name
                                        </label>
                                    </div>
                                )}
                                <div className="form-floating mb-3">
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        name="email"
                                        placeholder="name@example.com"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        autoComplete="off"
                                    />
                                    <label htmlFor="email">
                                        <FaEnvelope className="me-2" />
                                        Email address
                                    </label>
                                </div>
                                <div className="form-floating mb-3">
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="password"
                                        name="password"
                                        placeholder="Password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required
                                        autoComplete="off"
                                    />
                                    <label htmlFor="password">
                                        <FaLock className="me-2" />
                                        Password
                                    </label>
                                </div>
                                {!isLogin && (
                                    <div className="form-floating mb-3">
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            placeholder="Confirm Password"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            required
                                            autoComplete="off"
                                        />
                                        <label htmlFor="confirmPassword">
                                            <FaLock className="me-2" />
                                            Confirm Password
                                        </label>
                                    </div>
                                )}
                                {!isLogin && role === 'instructor' && (
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="expertise"
                                            name="expertise"
                                            placeholder="Your expertise area"
                                            value={formData.expertise}
                                            onChange={handleInputChange}
                                            required
                                            autoComplete="off"
                                        />
                                        <label htmlFor="expertise">
                                            <FaChalkboardTeacher className="me-2" />
                                            Expertise Area
                                        </label>
                                    </div>
                                )}
                                {error && (
                                    <div className="alert alert-danger small py-2" role="alert">
                                        {error}
                                    </div>
                                )}
                                <div className="d-grid gap-2 mt-3">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg rounded-pill fw-semibold"
                                        disabled={loading}
                                        style={{ minHeight: 48 }}
                                    >
                                        {loading ? (
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        ) : null}
                                        {isLogin ? 'Sign In' : 'Create Account'}
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className="card-footer text-center py-3 bg-light border-0">
                            <div className="small">
                                {isLogin ? (
                                    <>
                                        Don't have an account?{' '}
                                        <button
                                            className="btn btn-link p-0 fw-semibold"
                                            onClick={() => setIsLogin(false)}
                                            type="button"
                                        >
                                            Sign up!
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        Already have an account?{' '}
                                        <button
                                            className="btn btn-link p-0 fw-semibold"
                                            onClick={() => setIsLogin(true)}
                                            type="button"
                                        >
                                            Sign in!
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .alert, .alert-danger {
                    border: none !important;
                    box-shadow: none !important;
                    background: transparent !important;
                    color: #d32f2f !important;
                    font-size: 1rem;
                    margin: 0;
                    padding: 0.5rem 0;
                }
            `}</style>
        </div>
    );
};

export default Auth;